import { WorkflowEditActionSurveyModal } from "@/pages/workflows/edit/actions/WorkflowEditActionSurveyModal.tsx";
import { WorkflowEditActionTaskModal } from "@/pages/workflows/edit/actions/WorkflowEditActionTaskModal.tsx";
import { useModals } from "@skailer-com/uikit";
import { WorkflowEditActionGoogleWorkspaceModal } from "@/pages/workflows/edit/actions/WorkflowEditActionGoogleWorkspaceModal.tsx";
import { WorkflowEditActionHTTPRequestModal } from "@/pages/workflows/edit/actions/WorkflowEditActionHTTPRequestModal.tsx";
import { WorkflowEditActionInvitationModal } from "@/pages/workflows/edit/actions/WorkflowEditActionInvitationModal.tsx";
import { WorkflowEditActionPerformanceReviewEnrollModal } from "@/pages/workflows/edit/actions/WorkflowEditActionPerformanceReviewEnrollModal/WorkflowEditActionPerformanceReviewEnrollModal.tsx";
import { WorkflowEditActionPerformanceReviewModal } from "@/pages/workflows/edit/actions/WorkflowEditActionPerformanceReviewModal/WorkflowEditActionPerformanceReviewModal.tsx";
import { WorkflowEditActionRunWorkflowModal } from "@/pages/workflows/edit/actions/WorkflowEditActionRunWorkflowModal.tsx";
import { WorkflowEditActionSendNotificationModal } from "@/pages/workflows/edit/actions/WorkflowEditActionSendNotificationModal.tsx";
import { WorkflowEditActionSurveyEnrollModal } from "@/pages/workflows/edit/actions/WorkflowEditActionSurveyEnrollModal.tsx";
import {
  ApiWorkflowBlockActionTypes,
  ApiWorkflowMeetingDateReference,
  ApiWorkflowTriggerScheduleDayType,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { WorkflowEditActionApprovalModal } from "@/pages/workflows/edit/actions/WorkflowEditActionApprovalModal.tsx";
import { WorkflowEditActionEsignModal } from "@/pages/workflows/edit/actions/WorkflowEditActionEsignModal.tsx";
import { WorkflowEditActionMeetingModal } from "@/pages/workflows/edit/actions/WorkflowEditActionMeetingModal.tsx";
import { WorkflowEditActionCandidateEmailModal } from "@/pages/workflows/edit/actions/WorkflowEditActionCandidateEmailModal.tsx";
import { WorkflowEditActionCandidateStageModal } from "@/pages/workflows/edit/actions/WorkflowEditActionCandidateStageModal.tsx";
import {
  ApiWorkflowBlockActionCandidateEmail,
  ApiWorkflowBlockActionCandidateStageChange,
  ApiWorkflowBlockActionCandidateStageChangeMode,
  ApiWorkflowBlockActionMeeting,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiEventVisibility } from "@/types/event.types.ts";

const defaultMeetingAction: ApiWorkflowBlockActionMeeting = {
  organizer: null,
  calendar: null,
  title: "",
  description: null,
  location: null,
  visibility: ApiEventVisibility.public,
  attendee_rule_groups: [],
  external_attendee_emails: [],
  start_time: "09:00:00",
  duration_minutes: 30,
  is_conference: false,
  meeting_date: {
    reference: ApiWorkflowMeetingDateReference.block_execution,
    entity_type: null,
    entity_field: null,
    offset_days: 0,
    day_type: ApiWorkflowTriggerScheduleDayType.calendar,
  },
};

const defaultCandidateEmailAction: ApiWorkflowBlockActionCandidateEmail = {
  from_email: null,
  sender_id: null,
  subject: "",
  body: "",
  template_id: null,
  file_uuids: [],
};

const defaultCandidateStageChangeAction: ApiWorkflowBlockActionCandidateStageChange = {
  mode: ApiWorkflowBlockActionCandidateStageChangeMode.change_stage,
  stage_id: null,
  disqualification_reason_id: null,
  reserved: false,
  comment: null,
};

export const useWorkflowBlockEdit = () => {
  const { openModal } = useModals();

  const handleWorkflowBlockEdit = (
    block: ApiWorkflowBlock,
    workflowVersion: ApiWorkflowVersion
  ) => {
    if (block.action_type === ApiWorkflowBlockActionTypes.performance_review_enroll) {
      openModal(WorkflowEditActionPerformanceReviewEnrollModal, {
        workflowVersion: workflowVersion,
        block: block,
      });
    } else if (block.action_type === ApiWorkflowBlockActionTypes.survey_enroll) {
      openModal(WorkflowEditActionSurveyEnrollModal, {
        workflowVersion: workflowVersion,
        block: block,
      });
    } else if (block.action_notification) {
      openModal(WorkflowEditActionSendNotificationModal, {
        workflowVersion: workflowVersion,
        block: block,
        actionNotification: block.action_notification!,
      });
    } else if (block.action_http_request) {
      openModal(WorkflowEditActionHTTPRequestModal, {
        workflowVersion: workflowVersion,
        block: block,
        actionHTTPRequest: block.action_http_request!,
      });
    } else if (block.action_google_workspace) {
      openModal(WorkflowEditActionGoogleWorkspaceModal, {
        workflowVersion: workflowVersion,
        block: block,
        actionGoogleWorkspace: block.action_google_workspace!,
      });
    } else if (block.action_invitation) {
      openModal(WorkflowEditActionInvitationModal, {
        workflowVersion: workflowVersion,
        block: block,
        actionInvitation: block.action_invitation!,
      });
    } else if (block.action_survey) {
      openModal(WorkflowEditActionSurveyModal, {
        workflowVersion: workflowVersion,
        block: block,
        actionSurvey: block.action_survey!,
      });
    } else if (block.action_task) {
      openModal(WorkflowEditActionTaskModal, {
        workflowVersion: workflowVersion,
        block: block,
        actionTask: block.action_task!,
      });
    } else if (block.action_performance_review) {
      openModal(WorkflowEditActionPerformanceReviewModal, {
        workflowVersion: workflowVersion,
        block: block,
        actionPerformanceReview: block.action_performance_review,
      });
    } else if (block.action_type === ApiWorkflowBlockActionTypes.run_workflow) {
      openModal(WorkflowEditActionRunWorkflowModal, {
        workflowVersion: workflowVersion,
        block: block,
        actionRunWorkflow: block.action_run_workflow ?? {
          target_workflow: null,
        },
      });
    } else if (block.action_type === ApiWorkflowBlockActionTypes.survey_with_approval) {
      openModal(WorkflowEditActionApprovalModal, {
        workflowVersion: workflowVersion,
        block: block,
        actionApproval: block.action_survey_with_approval,
      });
    } else if (block.action_type === ApiWorkflowBlockActionTypes.esign) {
      openModal(WorkflowEditActionEsignModal, {
        workflowVersion: workflowVersion,
        block: block,
        actionEsign: block.action_esign,
      });
    } else if (block.action_type === ApiWorkflowBlockActionTypes.meeting) {
      openModal(WorkflowEditActionMeetingModal, {
        workflowVersion: workflowVersion,
        block: block,
        actionMeeting: block.action_meeting ?? defaultMeetingAction,
      });
    } else if (block.action_type === ApiWorkflowBlockActionTypes.candidate_email) {
      openModal(WorkflowEditActionCandidateEmailModal, {
        workflowVersion: workflowVersion,
        block: block,
        actionCandidateEmail: block.action_candidate_email ?? defaultCandidateEmailAction,
      });
    } else if (block.action_type === ApiWorkflowBlockActionTypes.candidate_stage_change) {
      openModal(WorkflowEditActionCandidateStageModal, {
        workflowVersion: workflowVersion,
        block: block,
        actionCandidateStageChange:
          block.action_candidate_stage_change ?? defaultCandidateStageChangeAction,
      });
    }
  };

  return { handleWorkflowBlockEdit };
};
